/**
 * CSV Utility Functions for Family Bulk Import
 */

/**
 * Generates CSV template with headers and example rows
 * @returns {string} CSV template content
 */
export const generateCSVTemplate = () => {
  const headers = [
    "EMAIL",
    "FIRST_NAME",
    "LAST_NAME",
    "CALLING_CODE",
    "CONTACT",
    "ALTERNATE_EMAIL",
    "ALTERNATE_FIRST_NAME",
    "ALTERNATE_LAST_NAME",
    "ALTERNATE_CALLING_CODE",
    "ALTERNATE_CONTACT",
    "ADDRESS_ONE",
    "ADDRESS_TWO",
    "CITY",
    "STATE",
    "POSTCODE",
    "Child 1 First Name",
    "Child 1 Last Name",
    "Child 1 Preferred Name",
    "Child 1 Date of Birth",
    "Child 1 Class",
    "Child 1 Preferences: MON",
    "Child 1 Preferences: TUE",
    "Child 1 Preferences: WED",
    "Child 1 Preferences: THU",
    "Child 1 Preferences: FRI",
    "Child 2 First Name",
    "Child 2 Last Name",
    "Child 2 Preferred Name",
    "Child 2 Date of Birth",
    "Child 2 Class",
    "Child 2 Preferences: MON",
    "Child 2 Preferences: TUE",
    "Child 2 Preferences: WED",
    "Child 2 Preferences: THU",
    "Child 2 Preferences: FRI",
    "Child 3 First Name",
    "Child 3 Last Name",
    "Child 3 Preferred Name",
    "Child 3 Date of Birth",
    "Child 3 Class",
    "Child 3 Preferences: MON",
    "Child 3 Preferences: TUE",
    "Child 3 Preferences: WED",
    "Child 3 Preferences: THU",
    "Child 3 Preferences: FRI",
    "Child 4 First Name",
    "Child 4 Last Name",
    "Child 4 Preferred Name",
    "Child 4 Date of Birth",
    "Child 4 Class",
    "Child 4 Preferences: MON",
    "Child 4 Preferences: TUE",
    "Child 4 Preferences: WED",
    "Child 4 Preferences: THU",
    "Child 4 Preferences: FRI",
  ];

  // Example row with 2 parents and 2 children
  // Total columns: 15 (parent/address) + 40 (4 children × 10 fields each: 5 basic + 5 preferences) = 55 columns
  const exampleRow = [
    // Primary parent (5 fields)
    "john.doe@example.com",
    "John",
    "Doe",
    "1",
    "234567890",
    // Alternate parent (5 fields)
    "jane.doe@example.com",
    "Jane",
    "Doe",
    "1",
    "234567891",
    // Address (5 fields)
    "123 Main Street",
    "Apt 4B",
    "Springfield",
    "IL",
    "62701",
    // Child 1 (10 fields: 5 basic + 5 preferences)
    "Alice",
    "Doe",
    "Ali",
    "2015-03-15",
    "Grade 3",
    "AM,PM",
    "AM,PM",
    "AM,PM",
    "AM,PM",
    "AM,PM",
    // Child 2 (10 fields: 5 basic + 5 preferences)
    "Bob",
    "Doe",
    "Bobby",
    "2017-06-20",
    "Grade 1",
    "AM",
    "AM,PM",
    "AM,ECA",
    "AM,PM",
    "AM",
    // Child 3 (10 fields) - empty
    "",
    "",
    "",
    "",
    "",
    "",
    "",
    "",
    "",
    "",
    // Child 4 (10 fields) - empty
    "",
    "",
    "",
    "",
    "",
    "",
    "",
    "",
    "",
    "",
  ];

  // Convert to CSV format (using tab separator as indicated by the user's format)
  // Ensure each row has exactly the same number of columns as headers
  const csvRows = [headers, exampleRow];
  
  // Verify row lengths match header length
  const headerCount = headers.length;
  csvRows.forEach((row, index) => {
    if (row.length !== headerCount) {
      console.warn(`Row ${index} has ${row.length} columns, expected ${headerCount}`);
    }
  });
  
  // Join with tabs - no quotes for better Excel compatibility
  return csvRows.map((row) => {
    // Pad row if needed to match header count
    const paddedRow = [...row];
    while (paddedRow.length < headerCount) {
      paddedRow.push("");
    }
    // Trim if too long
    if (paddedRow.length > headerCount) {
      paddedRow.splice(headerCount);
    }
    return paddedRow.map((cell) => String(cell || "")).join("\t");
  }).join("\n");
};

/**
 * Parses CSV file content (supports both comma and tab separators)
 * @param {string} csvContent - CSV file content as string
 * @returns {Array<Object>} Array of parsed row objects
 */
export const parseCSVFile = (csvContent) => {
  const lines = csvContent.split("\n").filter((line) => line.trim() !== "");
  if (lines.length === 0) {
    throw new Error("CSV file is empty");
  }

  // Detect separator (tab or comma)
  const firstLine = lines[0];
  const hasTabs = firstLine.includes("\t");
  const separator = hasTabs ? "\t" : ",";

  // Parse header
  const headers = parseCSVLine(lines[0], separator);
  const expectedHeaders = [
    "EMAIL",
    "FIRST_NAME",
    "LAST_NAME",
    "CALLING_CODE",
    "CONTACT",
    "ALTERNATE_EMAIL",
    "ALTERNATE_FIRST_NAME",
    "ALTERNATE_LAST_NAME",
    "ALTERNATE_CALLING_CODE",
    "ALTERNATE_CONTACT",
    "ADDRESS_ONE",
    "ADDRESS_TWO",
    "CITY",
    "STATE",
    "POSTCODE",
    "Child 1 First Name",
    "Child 1 Last Name",
    "Child 1 Preferred Name",
    "Child 1 Date of Birth",
    "Child 1 Class",
    "Child 1 Preferences: MON",
    "Child 1 Preferences: TUE",
    "Child 1 Preferences: WED",
    "Child 1 Preferences: THU",
    "Child 1 Preferences: FRI",
    "Child 2 First Name",
    "Child 2 Last Name",
    "Child 2 Preferred Name",
    "Child 2 Date of Birth",
    "Child 2 Class",
    "Child 2 Preferences: MON",
    "Child 2 Preferences: TUE",
    "Child 2 Preferences: WED",
    "Child 2 Preferences: THU",
    "Child 2 Preferences: FRI",
    "Child 3 First Name",
    "Child 3 Last Name",
    "Child 3 Preferred Name",
    "Child 3 Date of Birth",
    "Child 3 Class",
    "Child 3 Preferences: MON",
    "Child 3 Preferences: TUE",
    "Child 3 Preferences: WED",
    "Child 3 Preferences: THU",
    "Child 3 Preferences: FRI",
    "Child 4 First Name",
    "Child 4 Last Name",
    "Child 4 Preferred Name",
    "Child 4 Date of Birth",
    "Child 4 Class",
    "Child 4 Preferences: MON",
    "Child 4 Preferences: TUE",
    "Child 4 Preferences: WED",
    "Child 4 Preferences: THU",
    "Child 4 Preferences: FRI",
  ];

  // Create a mapping of normalized headers to original headers
  const headerMap = new Map();
  headers.forEach((header) => {
    const normalized = header.trim().toUpperCase().replace(/\s+/g, " ");
    headerMap.set(normalized, header.trim());
  });

  // Validate headers (case-insensitive comparison, normalize spaces)
  const normalizedHeaders = Array.from(headerMap.keys());
  const normalizedExpected = expectedHeaders.map((h) => h.trim().toUpperCase().replace(/\s+/g, " "));
  
  // Check if all expected headers are present (allow extra columns)
  const missingHeaders = normalizedExpected.filter(
    (expected) => !normalizedHeaders.includes(expected)
  );
  
  if (missingHeaders.length > 0) {
    throw new Error(`Invalid CSV format. Missing required columns: ${missingHeaders.join(", ")}`);
  }

  // Parse data rows
  const rows = [];
  for (let i = 1; i < lines.length; i++) {
    const values = parseCSVLine(lines[i], separator);
    
    // Create row object with normalized header keys
    const row = {};
    headers.forEach((header, index) => {
      const normalized = header.trim().toUpperCase().replace(/\s+/g, " ");
      row[normalized] = values[index]?.trim() || "";
    });
    rows.push(row);
  }

  return rows;
};

/**
 * Parses a single CSV line handling quoted values
 * @param {string} line - CSV line
 * @param {string} separator - Separator character (comma or tab)
 * @returns {Array<string>} Array of parsed values
 */
const parseCSVLine = (line, separator = ",") => {
  const values = [];
  let current = "";
  let inQuotes = false;

  for (let i = 0; i < line.length; i++) {
    const char = line[i];
    const nextChar = line[i + 1];

    if (char === '"') {
      if (inQuotes && nextChar === '"') {
        // Escaped quote
        current += '"';
        i++; // Skip next quote
      } else {
        // Toggle quote state
        inQuotes = !inQuotes;
      }
    } else if (char === separator && !inQuotes) {
      // End of field
      values.push(current);
      current = "";
    } else {
      current += char;
    }
  }

  // Push last field
  values.push(current);
  return values;
};

/**
 * Validates CSV data structure and required fields
 * @param {Array<Object>} rows - Parsed CSV rows
 * @returns {Object} Validation result with isValid and errors
 */
export const validateCSVData = (rows) => {
  const errors = [];
  const requiredFields = [
    "EMAIL",
    "FIRST_NAME",
    "LAST_NAME",
  ];

  if (!rows || rows.length === 0) {
    return {
      isValid: false,
      errors: ["CSV file contains no data rows"],
    };
  }

  rows.forEach((row, index) => {
    const rowNum = index + 2; // +2 because row 1 is header

    // Normalize field names for lookup
    const normalizeField = (field) => field.toUpperCase().replace(/\s+/g, " ");

    // Check required fields
    requiredFields.forEach((field) => {
      const normalizedField = normalizeField(field);
      if (!row[normalizedField] || row[normalizedField].trim() === "") {
        errors.push(`Row ${rowNum}: Missing required field "${field}"`);
      }
    });

    // Validate email format for primary parent
    const emailField = normalizeField("EMAIL");
    if (row[emailField] && row[emailField].trim() !== "") {
      const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
      if (!emailRegex.test(row[emailField].trim())) {
        errors.push(`Row ${rowNum}: Invalid email format "${row[emailField]}"`);
      }
    }

    // Validate alternate email format if provided
    const altEmailField = normalizeField("ALTERNATE_EMAIL");
    if (row[altEmailField] && row[altEmailField].trim() !== "") {
      const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
      if (!emailRegex.test(row[altEmailField].trim())) {
        errors.push(`Row ${rowNum}: Invalid alternate email format "${row[altEmailField]}"`);
      }
    }

    // Check if at least one child is provided
    let hasChild = false;
    for (let i = 1; i <= 4; i++) {
      const childFirstNameField = normalizeField(`Child ${i} First Name`);
      const childLastNameField = normalizeField(`Child ${i} Last Name`);
      if (row[childFirstNameField] && row[childFirstNameField].trim() !== "") {
        hasChild = true;
        // If first name exists, last name should also exist
        if (!row[childLastNameField] || row[childLastNameField].trim() === "") {
          errors.push(`Row ${rowNum}: Child ${i} has first name but missing last name`);
        }
        break;
      }
    }

    if (!hasChild) {
      errors.push(`Row ${rowNum}: At least one child is required`);
    }
  });

  return {
    isValid: errors.length === 0,
    errors,
  };
};

/**
 * Transforms CSV rows into family objects
 * @param {Array<Object>} rows - Parsed CSV rows
 * @returns {Array<Object>} Array of family objects
 */
export const groupCSVRowsByFamily = (rows) => {
  // Helper function to normalize field names
  const getField = (row, fieldName) => {
    const normalized = fieldName.toUpperCase().replace(/\s+/g, " ");
    return row[normalized] || "";
  };

  const families = rows.map((row) => {
    // Build address from components
    const addressParts = [
      getField(row, "ADDRESS_ONE"),
      getField(row, "ADDRESS_TWO"),
      getField(row, "CITY"),
      getField(row, "STATE"),
      getField(row, "POSTCODE"),
    ].filter((part) => part.trim() !== "");
    const address = addressParts.join(", ");

    // Build parents array
    const parents = [];

    // Primary parent
    const email = getField(row, "EMAIL");
    if (email && email.trim() !== "") {
      parents.push({
        firstName: getField(row, "FIRST_NAME").trim(),
        lastName: getField(row, "LAST_NAME").trim(),
        preferredName: "",
        email: email.trim(),
        callingCode: getField(row, "CALLING_CODE").trim(),
        phoneNumber: getField(row, "CONTACT").trim(),
      });
    }

    // Alternate parent (if provided)
    const altEmail = getField(row, "ALTERNATE_EMAIL");
    if (altEmail && altEmail.trim() !== "") {
      parents.push({
        firstName: getField(row, "ALTERNATE_FIRST_NAME").trim(),
        lastName: getField(row, "ALTERNATE_LAST_NAME").trim(),
        preferredName: "",
        email: altEmail.trim(),
        callingCode: getField(row, "ALTERNATE_CALLING_CODE").trim(),
        phoneNumber: getField(row, "ALTERNATE_CONTACT").trim(),
      });
    }

    // Build children array (up to 4 children)
    const children = [];
    for (let i = 1; i <= 4; i++) {
      const firstName = getField(row, `Child ${i} First Name`).trim();
      const lastName = getField(row, `Child ${i} Last Name`).trim();

      if (firstName && lastName) {
        children.push({
          firstName: firstName,
          lastName: lastName,
          preferredName: getField(row, `Child ${i} Preferred Name`).trim(),
          class: getField(row, `Child ${i} Class`).trim(),
          dob: getField(row, `Child ${i} Date of Birth`).trim(),
          grade: "",
          note: "",
          preferences: "",
          alightPermission: false,
          comment: "",
        });
      }
    }

    return {
      address: address || "No address provided",
      parent: parents,
      child: children,
    };
  });

  return families;
};

/**
 * Downloads the failed families CSV file from base64 content
 * @param {Object} fileData - The failedFamiliesFile object from API response
 * @param {Function} onError - Optional error callback function
 * @returns {boolean} True if download was successful, false otherwise
 */
export const downloadFailedFamiliesFile = (fileData, onError = null) => {
  try {
    // Validate file data
    if (!fileData || !fileData.content) {
      const error = new Error('Invalid file data');
      if (onError) {
        onError(error);
      } else {
        console.error('Error downloading failed families file:', error);
        alert('Error report is not available. Please contact support.');
      }
      return false;
    }

    // Decode base64 content
    const base64Content = fileData.content;
    const binaryString = atob(base64Content);
    
    // Convert to blob
    const bytes = new Uint8Array(binaryString.length);
    for (let i = 0; i < binaryString.length; i++) {
      bytes[i] = binaryString.charCodeAt(i);
    }
    const blob = new Blob([bytes], { type: fileData.mimeType || 'text/csv' });
    
    // Create download link
    const url = window.URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    link.download = fileData.filename || 'failed-families.csv';
    
    // Trigger download
    document.body.appendChild(link);
    link.click();
    
    // Cleanup
    document.body.removeChild(link);
    window.URL.revokeObjectURL(url);
    
    return true;
  } catch (error) {
    console.error('Error downloading failed families file:', error);
    
    // User-friendly error message
    const errorMessage = error.message === 'Invalid file data'
      ? 'Error report is not available. Please contact support.'
      : 'Failed to download error report. Please try again.';
    
    if (onError) {
      onError(error);
    } else {
      alert(errorMessage);
    }
    
    return false;
  }
};
